// src/components/ModalLocation.js
import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, OverlayView, useLoadScript, Autocomplete } from '@react-google-maps/api';
import { IoLocationSharp, IoCloseCircle } from "react-icons/io5";
import './ModalLocation.css';
import { DEFAULT_COORDS } from '../context/LocationContext'; // <<< IMPORTA TU DEFAULT_COORDS

const mapContainerStyle = { width: '100%', height: '100%' };
// Ahora usamos exactamente los mismos coords por defecto que en LocationContext
const defaultCenter = DEFAULT_COORDS;

const libraries = ['places'];

const ModalLocation = ({ initialLocation, onAccept, onCancel, hideRange }) => {
  const [address, setAddress] = useState(initialLocation?.address || "");
  const [coordinates, setCoordinates] = useState({
    lat: initialLocation?.lat ?? defaultCenter.lat,
    lng: initialLocation?.lng ?? defaultCenter.lng,
  });
  const [radius, setRadius] = useState(initialLocation?.radius || 1000);

  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    console.log("Coordenadas actuales:", coordinates);
  }, [coordinates]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Al cargar la API, si no tenemos address, reverse-geocode para obtener la ciudad
  useEffect(() => {
    if (!isLoaded) return;
    // Sólo si no venía dirección
    if (!initialLocation?.address) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: coordinates }, (results, status) => {
        if (status === 'OK' && results[0]) {
          // Extraemos city y provincia
          const comps = results[0].address_components;
          const cityComp = comps.find(c => c.types.includes('locality'))
            || comps.find(c => c.types.includes('administrative_area_level_3'));
          const provComp = comps.find(c => c.types.includes('administrative_area_level_2'));
          const city     = cityComp ? cityComp.long_name : '';
          const province = provComp ? provComp.long_name : '';
          const label = [city, province].filter(Boolean).join(', ');
          setAddress(label || results[0].formatted_address);
        }
      });
    }
  }, [isLoaded, coordinates, initialLocation]);

  const [autocomplete, setAutocomplete] = useState(null);

  const onLoadAutocomplete = useCallback((autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  }, []);

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      setAddress(place.formatted_address);
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setCoordinates({ lat, lng });
      }
    } else {
      console.log('Autocomplete no está disponible.');
    }
  };

  // Función para usar la ubicación actual del usuario
  const handleUseCurrentLocation = () => {
    console.log('👉 handleUseCurrentLocation disparado');
    console.log('   Before geolocation → address:', address, 'coords:', coordinates);

    if (!navigator.geolocation) {
      console.error('🚫 Geolocalización no soportada');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const currentCoords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        console.log('✅ Geolocalización exitosa:', currentCoords);
        setCoordinates(currentCoords);

        // Reverse-geocode para obtener la dirección
        if (window.google?.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: currentCoords }, (results, status) => {
            console.log('🗺️ Geocoder status:', status, 'results:', results);
            if (status === 'OK' && results[0]) {
              console.log('   Dirección encontrada:', results[0].formatted_address);
              const comps = results[0].address_components;
              // Intentamos localizar primero la 'locality' (ciudad)
              const cityComp = comps.find(c => c.types.includes('locality'))
                || comps.find(c => c.types.includes('administrative_area_level_3'));
              // Y luego la 'administrative_area_level_2' (provincia)
              const provComp = comps.find(c => c.types.includes('administrative_area_level_2'));
              const city     = cityComp ? cityComp.long_name : '';
              const province = provComp ? provComp.long_name : '';
              // Construimos "Ciudad, Provincia"
              const label = [city, province].filter(Boolean).join(', ');
              setAddress(label || '');
            } else {
              console.warn('   Fallback address (no results): usando ciudad por defecto');
              setAddress('Mierda City');  // o la ciudad que prefieras
            }
          });
        } else {
          console.warn('⚠️ API de Google Maps no cargada, usando texto genérico');
          setAddress('API no cargada');
        }
      },
      (error) => {
        console.error('❌ Error en getCurrentPosition:', error);
      },
      { timeout: 10000 }
    );

    setPermissionDenied(false);  // reseteamos aviso
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(({ state }) => {
          if (state === 'denied') {
            // activamos la bandera para que pinte el texto en UI
            setPermissionDenied(true);
          } else {
            requestCurrentPosition();
          }
        })
        .catch(() => {
          requestCurrentPosition();
        });
    } else {
      requestCurrentPosition();
    }

  };

  const requestCurrentPosition = () => {
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        // … tu lógica exitosa …
      },
      (err) => {
        console.error('Error obteniendo la ubicación actual:', err);
        // si viene error directamente, también mostramos aviso
        setPermissionDenied(true);
      }
    );
  };

  const handleClear = () => {
    setAddress("");
    setCoordinates({ lat: null, lng: null });
  };

  const handleAccept = () => {
    console.log("ModalLocation: Aceptar. Nueva ubicación:", { address, ...coordinates, radius });
    onAccept({ address, ...coordinates, radius });
  };

  if (loadError) return <div>Error cargando el mapa</div>;
  if (!isLoaded) return <div>Cargando mapa...</div>;

  // Antes de renderizar el mapa, calculamos un centro "seguro":
  const mapCenter = {
    lat: typeof coordinates.lat === 'number' ? coordinates.lat : defaultCenter.lat,
    lng: typeof coordinates.lng === 'number' ? coordinates.lng : defaultCenter.lng,
  };

  return (
    <div className="modal-location">

      <div className="location-map-container">
        <div className="autocomplete-container">
          <div className="autocomplete-input-container">
            <Autocomplete
              onLoad={onLoadAutocomplete}
              onPlaceChanged={onPlaceChanged}
              options={{
                types: ['(cities)'],
                componentRestrictions: { country: 'es' }
              }}
            >
              <input
                type="text"
                placeholder={address ? '' : "Introduce tu dirección..."}
                className="location-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </Autocomplete>
            {/* Botón de limpiar el input, se muestra solo si hay texto */}
            {address && (
              <button
                className="clear-button"
                onClick={() => setAddress("")}
                title="Limpiar dirección"
              >
                <IoCloseCircle size={20} />
              </button>
            )}
          </div>
        </div>
        <div className="map-container">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={10}
            options={{
              disableDefaultUI: true,   // Desactiva todos los controles por defecto
              draggable: false,         // Evita que el usuario desplace el mapa
              clickableIcons: false,    // Desactiva la interacción con iconos en el mapa
            }}
          >
            {/* Marcador fijo en el centro usando OverlayView */}
            <OverlayView
              position={coordinates}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} // Prueba este pane si OVERLAY_LAYER no funciona bien
              getPixelPositionOffset={(width, height) => ({
                x: -width / 2,
                y: -height / 2,
              })}
            >
              <div className="custom-marker">
                <IoLocationSharp size={24} style={{ color: 'red' }} />
              </div>
            </OverlayView>
          </GoogleMap>
        </div>
      </div>

      {/* Sección para el slider y botón de ubicación actual */}
      <div className="range-location-container">
        {!hideRange && (
          <div className="range-controls">
          {/* Primera fila: Label a la izquierda y Slider a la derecha */}
          <div className="range-first-row">
            <span className="range-label">Rango (km):</span>
            <input
              type="range"
              min="0"
              max="1000"
              step="10"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="range-slider"
            />
          </div>
          {/* Segunda fila: Input numérico, alineado a la izquierda */}
          <div className="range-second-row">
            <input
              type="number"
              min="0"
              max="1000"
              step="10"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="range-number-input"
            />
          </div>
        </div>
        )}

        {/* ─── NUEVO: Botón “Borrar” si venimos de ModalUserDetail ─────── */}
        {hideRange && (
          <div className="clear-location-wrapper">
            <button
              className="clear-location-button"
              onClick={handleClear}
            >
              Borrar dirección
            </button>
          </div>
        )}

        <div className="current-location-wrapper">
          <button onClick={handleUseCurrentLocation} title="Usar mi ubicación actual">
            <IoLocationSharp size={30} />
          </button>
          {permissionDenied && (
            <div className="permission-warning">
              Actualice permisos de ubicación
            </div>
          )}
        </div>
      </div>


      <div className="modal-location-buttons">
        <button className="cancel-button" onClick={onCancel}>Cancelar</button>
        <button className="accept-button" onClick={handleAccept}>Aceptar</button>
      </div>
    </div>
  );
};

export default ModalLocation;
